<template>
  <div class="teaching-plan-container">
    <!-- 教学计划头部 -->
    <div class="plan-header">
      <h3>教学计划</h3>
      <div class="header-actions">
        <el-button
          type="primary"
          size="small"
          icon="el-icon-plus"
          @click="showCreatePlanDialog">创建计划</el-button>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-refresh"
          @click="loadTeachingPlan">刷新</el-button>
      </div>
    </div>

    <!-- 计划选择器 -->
    <div class="plan-selector" v-if="plans.length > 0">
      <el-select v-model="selectedPlanId" placeholder="请选择教学计划" @change="handlePlanChange" style="width: 300px;">
        <el-option
          v-for="plan in plans"
          :key="plan.id"
          :label="plan.planName"
          :value="plan.id">
          <span style="float: left">{{ plan.planName }}</span>
          <span style="float: right; color: #8492a6; font-size: 13px">{{ plan.academicYear }} {{ plan.semester }}</span>
        </el-option>
      </el-select>
    </div>

    <!-- 日历控件 -->
    <div class="calendar-container" v-if="selectedPlanId">
      <div class="calendar-header">
        <el-date-picker
          v-model="selectedYearMonth"
          type="month"
          placeholder="选择年月"
          format="yyyy年MM月"
          value-format="yyyy-MM"
          @change="handleMonthChange"
          style="width: 200px;">
        </el-date-picker>
        <div class="activity-count">
          <i class="el-icon-calendar"></i>
          <span>本月共 {{ getActivitiesByMonth().length }} 个教学活动</span>
        </div>
      </div>

      <!-- Element UI 日历组件 -->
      <el-calendar v-model="currentDate" class="calendar">
        <template slot="dateCell" slot-scope="{date, data}">
          <div class="calendar-day" @click="selectDate(date)">
            <div class="day-number">{{ data.day.split('-')[2] }}</div>
            <div class="day-activities">
              <div
                v-for="activity in getActivitiesByDate(date)"
                :key="activity.id"
                class="activity-item"
                :class="getActivityTypeClass(activity.activityType)"
                @click.stop="editActivity(activity)"
                :title="getActivityTooltip(activity)">
                <i :class="getActivityIcon(activity.activityType)"></i>
                <span class="activity-type">{{ activity.activityType }}</span>
                <span class="activity-title" v-if="activity.title">{{ activity.title }}</span>
                <span class="activity-location" v-if="activity.location">{{ activity.location }}</span>
              </div>
            </div>
          </div>
        </template>
      </el-calendar>
    </div>

    <!-- 创建计划对话框 -->
    <el-dialog
      title="创建教学计划"
      :visible.sync="createPlanDialogVisible"
      width="600px"
      :modal="false"
      @close="resetCreatePlanForm">
      <el-form :model="createPlanForm" :rules="createPlanRules" ref="createPlanForm" label-width="100px">
        <el-form-item label="计划名称" prop="planName">
          <el-input v-model="createPlanForm.planName" placeholder="请输入计划名称"></el-input>
        </el-form-item>
        <el-form-item label="学年" prop="academicYear">
          <el-input v-model="createPlanForm.academicYear" placeholder="如：2024-2025"></el-input>
        </el-form-item>
        <el-form-item label="学期" prop="semester">
          <el-select v-model="createPlanForm.semester" placeholder="请选择学期" style="width: 100%;">
            <el-option label="春季学期" value="春季学期"></el-option>
            <el-option label="夏季学期" value="夏季学期"></el-option>
            <el-option label="秋季学期" value="秋季学期"></el-option>
            <el-option label="冬季学期" value="冬季学期"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="开始日期" prop="startDate">
          <el-date-picker
            v-model="createPlanForm.startDate"
            type="date"
            placeholder="选择开始日期"
            value-format="yyyy-MM-dd"
            style="width: 100%;">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="结束日期" prop="endDate">
          <el-date-picker
            v-model="createPlanForm.endDate"
            type="date"
            placeholder="选择结束日期"
            value-format="yyyy-MM-dd"
            style="width: 100%;">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="计划描述">
          <el-input
            type="textarea"
            v-model="createPlanForm.description"
            :rows="4"
            placeholder="请输入计划描述">
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="createPlanDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="createPlan" :loading="creating">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 编辑活动对话框 -->
    <el-dialog
      :title="activityDialogTitle"
      :visible.sync="activityDialogVisible"
      width="800px"
      @close="resetActivityForm">
      <el-form :model="activityForm" :rules="activityRules" ref="activityForm" label-width="100px">
        <el-form-item label="活动日期" v-if="editingActivityId">
          <el-date-picker
            v-model="activityForm.activityDate"
            type="date"
            placeholder="选择活动日期"
            value-format="yyyy-MM-dd"
            style="width: 100%;">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="活动日期" v-else>
          <span style="color: #606266; font-size: 14px;">{{ activityForm.activityDate }}</span>
        </el-form-item>
        <el-form-item label="活动类型" prop="activityType">
          <el-select v-model="activityForm.activityType" placeholder="请选择活动类型" style="width: 100%;">
            <el-option label="理论课" value="理论课"></el-option>
            <el-option label="实践课" value="实践课"></el-option>
            <el-option label="实验课" value="实验课"></el-option>
            <el-option label="讨论课" value="讨论课"></el-option>
            <el-option label="复习课" value="复习课"></el-option>
            <el-option label="考试" value="考试"></el-option>
            <el-option label="作业" value="作业"></el-option>
            <el-option label="其他" value="其他"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="活动标题">
          <el-input v-model="activityForm.title" placeholder="请输入活动标题（可选）"></el-input>
        </el-form-item>
        <el-form-item label="活动描述">
          <el-input
            type="textarea"
            v-model="activityForm.description"
            :rows="4"
            placeholder="请输入活动描述">
          </el-input>
        </el-form-item>
        <el-form-item label="课时时长">
          <el-input-number v-model="activityForm.duration" :min="0" :max="300" placeholder="分钟"></el-input-number>
        </el-form-item>
        <el-form-item label="上课地点">
          <el-input v-model="activityForm.location" placeholder="请输入上课地点"></el-input>
        </el-form-item>

        <!-- 关联资源 -->
        <el-form-item label="教学资源">
          <el-select
            v-model="activityForm.resources"
            multiple
            filterable
            placeholder="请选择教学资源"
            style="width: 100%;">
            <el-option
              v-for="resource in availableResources"
              :key="resource.id"
              :label="resource.originalFileName || resource.fileName"
              :value="resource.id">
            </el-option>
          </el-select>
          <!-- 显示已选择的资源 -->
          <div v-if="activityForm.resources && activityForm.resources.length > 0" class="selected-items">
            <div class="selected-item" v-for="resourceId in activityForm.resources" :key="resourceId">
              <i class="el-icon-document"></i>
              <span>{{ getResourceName(resourceId) }}</span>
              <i class="el-icon-close" @click="removeResource(resourceId)"></i>
            </div>
          </div>
        </el-form-item>

        <!-- 关联作业 -->
        <el-form-item label="关联作业">
          <el-select
            v-model="activityForm.homeworks"
            multiple
            filterable
            placeholder="请选择作业"
            style="width: 100%;">
            <el-option
              v-for="homework in availableHomeworks"
              :key="homework.id"
              :label="homework.name || homework.title"
              :value="homework.id">
            </el-option>
          </el-select>
          <!-- 显示已选择的作业 -->
          <div v-if="activityForm.homeworks && activityForm.homeworks.length > 0" class="selected-items">
            <div class="selected-item" v-for="homeworkId in activityForm.homeworks" :key="homeworkId">
              <i class="el-icon-folder"></i>
              <span>{{ getHomeworkName(homeworkId) }}</span>
              <i class="el-icon-close" @click="removeHomework(homeworkId)"></i>
            </div>
          </div>
        </el-form-item>

      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="activityDialogVisible = false">取 消</el-button>
        <el-button type="danger" @click="deleteActivity" v-if="editingActivityId">删 除</el-button>
        <el-button type="primary" @click="saveActivity" :loading="saving">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getTeachingPlans, createTeachingPlan, getActivitiesByPlan, createActivity, updateActivity, deleteActivity, getResourcesByCourse, getHomeworksByCourse } from '@/api/teachingPlan'

export default {
  name: 'TeachingPlan',
  props: {
    courseId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      plans: [],
      selectedPlanId: null,
      currentPlan: null,
      activities: [],
      selectedYearMonth: new Date().toISOString().slice(0, 7),
      currentDate: new Date(),
      createPlanDialogVisible: false,
      activityDialogVisible: false,
      activityDialogTitle: '新建教学活动',
      editingActivityId: null,
      creating: false,
      saving: false,
      createPlanForm: {
        planName: '',
        academicYear: '',
        semester: '',
        startDate: '',
        endDate: '',
        description: ''
      },
      createPlanRules: {
        planName: [
          { required: true, message: '请输入计划名称', trigger: 'blur' }
        ],
        academicYear: [
          { required: true, message: '请输入学年', trigger: 'blur' }
        ],
        semester: [
          { required: true, message: '请选择学期', trigger: 'blur' }
        ],
        startDate: [
          { required: true, message: '请选择开始日期', trigger: 'blur' }
        ],
        endDate: [
          { required: true, message: '请选择结束日期', trigger: 'blur' }
        ]
      },
      activityForm: {
        activityDate: '',
        activityType: '',
        title: '',
        description: '',
        duration: 90,
        location: '',
        resources: [],
        homeworks: []
      },
      activityRules: {
        activityDate: [
          { required: true, message: '请选择活动日期', trigger: 'blur' }
        ],
        activityType: [
          { required: true, message: '请选择活动类型', trigger: 'blur' }
        ]
      },
      availableResources: [],
      availableHomeworks: []
    }
  },
  created() {
    this.loadTeachingPlan()
    this.loadResources()
    this.loadHomeworks()
  },
  methods: {
    // 加载教学计划列表
    async loadTeachingPlan() {
      try {
        const response = await getTeachingPlans(this.courseId)
        if (response.code === 1) {
          this.plans = response.data || []
          if (this.plans.length > 0 && !this.selectedPlanId) {
            this.selectedPlanId = this.plans[0].id
            this.handlePlanChange()
          }
        }
      } catch (error) {
        console.error('加载教学计划失败:', error)
        this.$message.error('加载教学计划失败')
      }
    },

    // 创建教学计划
    async createPlan() {
      this.$refs.createPlanForm.validate(async (valid) => {
        if (valid) {
          this.creating = true
          try {
            const response = await createTeachingPlan({
              courseId: this.courseId,
              ...this.createPlanForm
            })
            if (response.code === 1) {
              this.$message.success('创建教学计划成功')
              this.createPlanDialogVisible = false
              this.loadTeachingPlan()
            } else {
              this.$message.error(response.msg || '创建教学计划失败')
            }
          } catch (error) {
            console.error('创建教学计划失败:', error)
            this.$message.error('创建教学计划失败')
          } finally {
            this.creating = false
          }
        }
      })
    },

    // 显示创建计划对话框
    showCreatePlanDialog() {
      this.createPlanDialogVisible = true
    },

    // 重置创建计划表单
    resetCreatePlanForm() {
      this.$refs.createPlanForm.resetFields()
      this.createPlanForm = {
        planName: '',
        academicYear: '',
        semester: '',
        startDate: '',
        endDate: '',
        description: ''
      }
    },

    // 计划变更处理
    async handlePlanChange() {
      if (this.selectedPlanId) {
        await this.loadActivities()
      }
    },

    // 加载活动列表
    async loadActivities() {
      try {
        const response = await getActivitiesByPlan(this.selectedPlanId)
        if (response.code === 1) {
          this.activities = response.data || []
        }
      } catch (error) {
        console.error('加载活动列表失败:', error)
        this.$message.error('加载活动列表失败')
      }
    },

    // 月份变更处理
    handleMonthChange(value) {
      this.selectedYearMonth = value
    },

    // 获取指定月份的活动
    getActivitiesByMonth() {
      if (!this.selectedYearMonth) return []
      return this.activities.filter(activity => {
        const activityMonth = activity.activityDate.slice(0, 7)
        return activityMonth === this.selectedYearMonth
      })
    },

    // 获取指定日期的活动
    getActivitiesByDate(date) {
      const dateStr = this.formatDate(date)
      return this.activities.filter(activity => activity.activityDate === dateStr)
    },

    // 格式化日期
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // 选择日期
    selectDate(date) {
      const dateStr = this.formatDate(date)
      this.activityDialogTitle = '新建教学活动'
      this.editingActivityId = null
      this.resetActivityForm()
      // 设置选中的日期
      this.activityForm.activityDate = dateStr
      this.activityDialogVisible = true
    },

    // 编辑活动
    editActivity(activity) {
      console.log('编辑活动 - 原始数据:', activity)
      console.log('编辑活动 - resources数据:', activity.resources)
      console.log('编辑活动 - homeworks数据:', activity.homeworks)
      console.log('编辑活动 - 当前可用资源列表:', this.availableResources)
      console.log('编辑活动 - 可用资源数量:', this.availableResources.length)

      this.editingActivityId = activity.id
      this.activityDialogTitle = '编辑教学活动'

      // 转换资源ID为数组，过滤掉无效值
      let resources = []
      if (activity.resources && Array.isArray(activity.resources) && activity.resources.length > 0) {
        // 检查第一个元素是否是对象
        if (typeof activity.resources[0] === 'object' && activity.resources[0] !== null) {
          // 如果是对象数组，提取ID
          resources = activity.resources
            .map(r => {
              const id = r.id || r.resourceId
              const num = typeof id === 'number' ? id : Number(id)
              return isNaN(num) ? null : num
            })
            .filter(r => r !== null)
        } else {
          // 如果是简单的值数组
          resources = activity.resources
            .map(r => {
              const num = typeof r === 'number' ? r : Number(r)
              return isNaN(num) ? null : num
            })
            .filter(r => r !== null)
        }
      }

      // 转换作业ID为数组，过滤掉无效值
      let homeworks = []
      if (activity.homeworks && Array.isArray(activity.homeworks) && activity.homeworks.length > 0) {
        // 检查第一个元素是否是对象
        if (typeof activity.homeworks[0] === 'object' && activity.homeworks[0] !== null) {
          // 如果是对象数组，提取ID
          homeworks = activity.homeworks
            .map(h => {
              const id = h.id || h.homeworkId
              const num = typeof id === 'number' ? id : Number(id)
              return isNaN(num) ? null : num
            })
            .filter(h => h !== null)
        } else {
          // 如果是简单的值数组
          homeworks = activity.homeworks
            .map(h => {
              const num = typeof h === 'number' ? h : Number(h)
              return isNaN(num) ? null : num
            })
            .filter(h => h !== null)
        }
      }

      console.log('编辑活动 - 转换后的resources:', resources)
      console.log('编辑活动 - 转换后的homeworks:', homeworks)

      this.activityForm = {
        activityDate: activity.activityDate,
        activityType: activity.activityType,
        title: activity.title || '',
        description: activity.description || '',
        duration: activity.duration || 90,
        location: activity.location || '',
        resources: resources,
        homeworks: homeworks
      }

      this.activityDialogVisible = true
    },

    // 保存活动
    async saveActivity() {
      this.$refs.activityForm.validate(async (valid) => {
        if (valid) {
          this.saving = true
          try {
            const data = {
              planId: this.selectedPlanId,
              ...this.activityForm
            }
            let response
            if (this.editingActivityId) {
              response = await updateActivity(this.editingActivityId, data)
            } else {
              response = await createActivity(data)
            }
            if (response.code === 1) {
              this.$message.success(this.editingActivityId ? '更新活动成功' : '创建活动成功')
              this.activityDialogVisible = false
              this.loadActivities()
            } else {
              this.$message.error(response.msg || '保存活动失败')
            }
          } catch (error) {
            console.error('保存活动失败:', error)
            this.$message.error('保存活动失败')
          } finally {
            this.saving = false
          }
        }
      })
    },

    // 删除活动
    async deleteActivity() {
      this.$confirm('确定要删除这个教学活动吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await deleteActivity(this.editingActivityId)
          if (response.code === 1) {
            this.$message.success('删除活动成功')
            this.activityDialogVisible = false
            this.loadActivities()
          } else {
            this.$message.error(response.msg || '删除活动失败')
          }
        } catch (error) {
          console.error('删除活动失败:', error)
          this.$message.error('删除活动失败')
        }
      })
    },

    // 重置活动表单
    resetActivityForm() {
      // 保存当前的活动日期
      const currentDate = this.activityForm.activityDate
      this.$refs.activityForm && this.$refs.activityForm.resetFields()
      this.activityForm = {
        activityDate: currentDate || '', // 保留选择的日期
        activityType: '',
        title: '',
        description: '',
        duration: 90,
        location: '',
        resources: [],
        homeworks: []
      }
    },

    // 获取活动类型样式类
    getActivityTypeClass(type) {
      const classMap = {
        '理论课': 'activity-theory',
        '实践课': 'activity-practice',
        '实验课': 'activity-experiment',
        '讨论课': 'activity-discussion',
        '复习课': 'activity-review',
        '考试': 'activity-exam',
        '作业': 'activity-homework',
        '其他': 'activity-other'
      }
      return classMap[type] || 'activity-other'
    },

    // 获取活动类型图标
    getActivityIcon(type) {
      const iconMap = {
        '理论课': 'el-icon-reading',
        '实践课': 'el-icon-edit-outline',
        '实验课': 'el-icon-location-outline',
        '讨论课': 'el-icon-chat-line-round',
        '复习课': 'el-icon-refresh',
        '考试': 'el-icon-document',
        '作业': 'el-icon-folder',
        '其他': 'el-icon-more'
      }
      return iconMap[type] || 'el-icon-more'
    },

    // 获取活动提示信息
    getActivityTooltip(activity) {
      let tooltip = `类型：${activity.activityType}`
      if (activity.title) {
        tooltip += `\n名称：${activity.title}`
      }
      if (activity.location) {
        tooltip += `\n地点：${activity.location}`
      }
      if (activity.duration) {
        tooltip += `\n时长：${activity.duration}分钟`
      }
      return tooltip
    },

    // 加载资源列表
    async loadResources() {
      try {
        const response = await getResourcesByCourse(this.courseId)
        console.log('加载资源列表响应:', response)
        if (response.code === 1) {
          this.availableResources = response.data || []
          console.log('可用资源列表:', this.availableResources)
          console.log('资源数量:', this.availableResources.length)
          // 打印每个资源的详细信息
          this.availableResources.forEach((resource, index) => {
            console.log(`资源${index + 1}:`, {
              id: resource.id,
              name: resource.originalFileName || resource.fileName,
              type: resource.fileType,
              url: resource.fileUrl
            })
          })
        }
      } catch (error) {
        console.error('加载资源列表失败:', error)
      }
    },

    // 加载作业列表
    async loadHomeworks() {
      try {
        const response = await getHomeworksByCourse(this.courseId)
        if (response.code === 1) {
          this.availableHomeworks = response.data || []
        }
      } catch (error) {
        console.error('加载作业列表失败:', error)
      }
    },

    // 根据ID获取资源名称
    getResourceName(resourceId) {
      const resource = this.availableResources.find(r => {
        return r.id === resourceId ||
               Number(r.id) === Number(resourceId) ||
               String(r.id) === String(resourceId)
      })
      return resource ? (resource.originalFileName || resource.fileName) : `资源${resourceId}`
    },

    // 根据ID获取作业名称
    getHomeworkName(homeworkId) {
      const homework = this.availableHomeworks.find(h => {
        return h.id === homeworkId ||
               Number(h.id) === Number(homeworkId) ||
               String(h.id) === String(homeworkId)
      })
      return homework ? (homework.name || homework.title) : `作业${homeworkId}`
    },

    // 移除资源
    removeResource(resourceId) {
      const index = this.activityForm.resources.indexOf(resourceId)
      if (index > -1) {
        this.activityForm.resources.splice(index, 1)
      }
    },

    // 移除作业
    removeHomework(homeworkId) {
      const index = this.activityForm.homeworks.indexOf(homeworkId)
      if (index > -1) {
        this.activityForm.homeworks.splice(index, 1)
      }
    }

  }
}
</script>

<style scoped lang="scss">
.teaching-plan-container {
  padding: 20px;

  .plan-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
    }

    .header-actions {
      .el-button {
        margin-left: 10px;
      }
    }
  }

  .plan-selector {
    margin-bottom: 20px;
  }

  .calendar-container {
    .calendar-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      .activity-count {
        color: #606266;
        font-size: 14px;

        i {
          margin-right: 5px;
        }
      }
    }

    .calendar {
      .calendar-day {
        height: 100%;
        cursor: pointer;
        padding: 5px;

        .day-number {
          font-weight: 600;
          margin-bottom: 5px;
        }

        .day-activities {
          .activity-item {
            font-size: 11px;
            padding: 3px 5px;
            margin-bottom: 2px;
            border-radius: 3px;
            cursor: pointer;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            line-height: 1.4;

            i {
              margin-right: 3px;
              font-size: 12px;
            }

            .activity-type {
              font-weight: 600;
              margin-right: 3px;
            }

            .activity-title {
              margin-right: 3px;
              color: inherit;
              opacity: 0.9;
            }

            .activity-location {
              margin-left: auto;
              opacity: 0.8;
              font-size: 10px;
            }

            &.activity-theory {
              background-color: #e3f2fd;
              color: #1976d2;
            }

            &.activity-practice {
              background-color: #f3e5f5;
              color: #7b1fa2;
            }

            &.activity-experiment {
              background-color: #e8f5e9;
              color: #388e3c;
            }

            &.activity-discussion {
              background-color: #fff3e0;
              color: #f57c00;
            }

            &.activity-review {
              background-color: #fce4ec;
              color: #c2185b;
            }

            &.activity-exam {
              background-color: #ffebee;
              color: #d32f2f;
            }

            &.activity-homework {
              background-color: #e0f2f1;
              color: #00796b;
            }

            &.activity-other {
              background-color: #f5f5f5;
              color: #616161;
            }
          }
        }
      }
    }
  }
}

// 已选择项的样式
.selected-items {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .selected-item {
    display: flex;
    align-items: center;
    padding: 5px 10px;
    background-color: #f0f9ff;
    border: 1px solid #bae6fd;
    border-radius: 4px;
    font-size: 13px;
    color: #0369a1;

    i {
      font-size: 14px;
      margin-right: 5px;

      &.el-icon-close {
        margin-left: 8px;
        margin-right: 0;
        cursor: pointer;
        color: #dc2626;
        font-size: 12px;

        &:hover {
          color: #991b1b;
        }
      }
    }

    span {
      flex: 1;
    }
  }
}
</style>
